import React from 'react';
import { useLocation } from 'react-router-dom';
import type { ListSnapshot } from './types';

export const writeListSnapshot = <TQuery>(sessionKey: string, snapshot: ListSnapshot<TQuery>) => {
    try {
        sessionStorage.setItem(sessionKey, JSON.stringify(snapshot));
    } catch {
        // sessionStorage unavailable (private mode, quota) — silently degrade
    }
};

export const readListSnapshot = <TQuery>(sessionKey: string): ListSnapshot<TQuery> | null => {
    try {
        const raw = sessionStorage.getItem(sessionKey);
        return raw ? (JSON.parse(raw) as ListSnapshot<TQuery>) : null;
    } catch {
        return null;
    }
};

export const getListBackTo = (
    locationState: unknown,
    sessionKey: string,
    fallback: string
): string => {
    const stateFrom = (locationState as Partial<ListSnapshot> | null)?.from;
    if (stateFrom) return stateFrom;
    return readListSnapshot(sessionKey)?.from ?? fallback;
};

export const useListSnapshot = <TQuery>(sessionKey: string): ListSnapshot<TQuery> | null => {
    const location = useLocation();

    const snapshot = React.useMemo<ListSnapshot<TQuery> | null>(() => {
        const fromState = location.state as Partial<ListSnapshot<TQuery>> | null;
        if (fromState?.from && fromState.listQuery && fromState.listKind) {
            return {
                from: fromState.from,
                listQuery: fromState.listQuery,
                listKind: fromState.listKind
            };
        }
        return readListSnapshot<TQuery>(sessionKey);
    }, [location.state, sessionKey]);

    React.useEffect(() => {
        if (snapshot) writeListSnapshot(sessionKey, snapshot);
    }, [snapshot, sessionKey]);

    return snapshot;
};
